import { useMutation } from "@apollo/client";
import Dialog from "components/generic/Dialog";
import gql from "graphql-tag";
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";

import { PrimaryButton, SecondaryButton } from "~/components/common/buttons";

// ── GraphQL ──────────────────────────────────────────────────────────────────

const PLAN_QA_LOG_IMPORT = gql`
  mutation PlanQaLogImport($portfolioId: ID!, $file: Upload!) {
    planQaLogImport(portfolioId: $portfolioId, file: $file) {
      errors
      rows {
        rowNumber
        groupNames
        logNames
        outcome
        detail
      }
    }
  }
`;

const CONFIRM_QA_LOG_IMPORT = gql`
  mutation ConfirmQaLogImport($portfolioId: ID!, $file: Upload!) {
    confirmQaLogImport(portfolioId: $portfolioId, file: $file) {
      errors
      createdCount
    }
  }
`;

// ── Types ─────────────────────────────────────────────────────────────────────

type Outcome = "CREATE" | "SKIP" | "ERROR";

interface PlanRow {
  rowNumber: number;
  groupNames: string[];
  logNames: string[];
  outcome: Outcome;
  detail: string;
}

type Step = "UPLOAD" | "REVIEW" | "DONE";

// ── Badge ─────────────────────────────────────────────────────────────────────

const OUTCOME_BADGE: Record<Outcome, { label: string; bg: string }> = {
  CREATE: { label: "Create", bg: "#28a745" },
  SKIP: { label: "No Change", bg: "#6c757d" },
  ERROR: { label: "Error", bg: "#dc3545" },
};

const Badge = ({ outcome }: { outcome: Outcome }) => {
  const { label, bg } = OUTCOME_BADGE[outcome] ?? OUTCOME_BADGE.ERROR;
  return (
    <span
      style={{
        display: "inline-block",
        background: bg,
        color: "#fff",
        borderRadius: 3,
        padding: "1px 7px",
        fontSize: "0.72rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

// ── Upload step ───────────────────────────────────────────────────────────────

const UploadStep = ({
  portfolioId,
  orgSlug,
  onPlan,
  onCancel,
}: {
  portfolioId: string;
  orgSlug: string;
  onPlan: (rows: PlanRow[], file: File) => void;
  onCancel: () => void;
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [planImport] = useMutation(PLAN_QA_LOG_IMPORT);
  const xlsxTemplateUrl = `/cleancapital/admin/${orgSlug}/qa-log-import/template.xlsx`;
  const csvTemplateUrl = `/cleancapital/admin/${orgSlug}/qa-log-import/template.xlsx?fmt=csv`;

  const onDrop = useCallback((accepted: File[]) => {
    if (accepted.length > 0) {
      setFile(accepted[0]);
      setErrors([]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".xlsx,.csv",
    multiple: false,
  });

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setErrors([]);
    try {
      const result = await planImport({ variables: { portfolioId, file } });
      const payload = result.data?.planQaLogImport;
      if (!payload) return;
      if (payload.errors?.length > 0) {
        setErrors(payload.errors);
        return;
      }
      onPlan(payload.rows, file);
    } catch (e: any) {
      setErrors([e?.message ?? "An unexpected error occurred."]);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <p style={{ margin: 0, color: "#666", fontSize: "0.875rem" }}>
        Upload a <strong>.xlsx</strong> or <strong>.csv</strong> file with
        columns:
        <strong> User Group(s)</strong> (required) and <strong>Log Name</strong>{" "}
        (optional). Comma-separate multiple groups in one cell; quote the cell
        in CSV files. Groups must already exist in the org.
      </p>

      <div
        {...getRootProps()}
        style={{
          border: `2px dashed ${isDragActive ? "#0d6efd" : "#ccc"}`,
          borderRadius: 6,
          padding: "2rem 1.5rem",
          textAlign: "center",
          cursor: "pointer",
          background: isDragActive ? "#f0f6ff" : "#fafafa",
          transition: "border-color 0.15s, background 0.15s",
        }}
      >
        <input {...getInputProps()} />
        {file ? (
          <p style={{ margin: 0, fontWeight: 500 }}>
            {file.name}
            <span
              style={{
                marginLeft: "0.5rem",
                color: "#888",
                fontSize: "0.8rem",
                fontWeight: 400,
              }}
            >
              (click to change)
            </span>
          </p>
        ) : isDragActive ? (
          <p style={{ margin: 0, color: "#0d6efd" }}>Drop it here…</p>
        ) : (
          <p style={{ margin: 0, color: "#555" }}>
            Drag & drop a <strong>.xlsx</strong> or <strong>.csv</strong> file
            here, or{" "}
            <span style={{ color: "#0d6efd", textDecoration: "underline" }}>
              browse
            </span>
          </p>
        )}
      </div>

      {errors.length > 0 && (
        <ul
          style={{
            margin: 0,
            padding: "0 0 0 1.25rem",
            color: "#dc3545",
            fontSize: "0.875rem",
          }}
        >
          {errors.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ fontSize: "0.8rem", color: "#666" }}>
          Download template:{" "}
          <a href={xlsxTemplateUrl} download style={{ marginRight: "0.5rem" }}>
            XLSX
          </a>
          ·{" "}
          <a href={csvTemplateUrl} download style={{ marginLeft: "0.5rem" }}>
            CSV
          </a>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <SecondaryButton onClick={onCancel} disabled={uploading}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={handleUpload} disabled={!file || uploading}>
            {uploading ? "Validating…" : "Upload & Preview"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

// ── Review step ───────────────────────────────────────────────────────────────

const ReviewStep = ({
  portfolioId,
  rows,
  file,
  onConfirmed,
  onBack,
}: {
  portfolioId: string;
  rows: PlanRow[];
  file: File;
  onConfirmed: (count: number) => void;
  onBack: () => void;
}) => {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmImport] = useMutation(CONFIRM_QA_LOG_IMPORT);

  const createCount = rows
    .filter((r) => r.outcome === "CREATE")
    .reduce((sum, r) => sum + r.groupNames.length, 0);
  const errorCount = rows.filter((r) => r.outcome === "ERROR").length;

  const handleConfirm = async () => {
    setConfirming(true);
    setError(null);
    try {
      const result = await confirmImport({ variables: { portfolioId, file } });
      const payload = result.data?.confirmQaLogImport;
      if (!payload) return;
      if (payload.errors?.length > 0) {
        setError(payload.errors[0]);
        return;
      }
      onConfirmed(payload.createdCount);
    } catch (e: any) {
      setError(e?.message ?? "An unexpected error occurred.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {/* Summary */}
      <div
        style={{
          display: "flex",
          gap: "1.5rem",
          fontSize: "0.875rem",
          color: "#495057",
        }}
      >
        <span>
          <strong style={{ color: "#28a745" }}>{createCount}</strong> to create
        </span>
        <span>
          <strong style={{ color: "#6c757d" }}>
            {rows.filter((r) => r.outcome === "SKIP").length}
          </strong>{" "}
          no change
        </span>
        {errorCount > 0 && (
          <span>
            <strong style={{ color: "#dc3545" }}>{errorCount}</strong> errors
          </span>
        )}
      </div>

      {/* Row table */}
      <div style={{ overflowX: "auto" }}>
        <table
          className="table table-sm table-bordered"
          style={{ fontSize: "0.875rem" }}
        >
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <th>Group(s)</th>
              <th>Log Name(s)</th>
              <th style={{ width: 100 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.rowNumber}>
                <td style={{ color: "#888" }}>{row.rowNumber}</td>
                <td>{row.groupNames.join(", ")}</td>
                <td>
                  {row.logNames.length > 0 ? (
                    row.logNames.map((n, i) => <div key={i}>{n}</div>)
                  ) : (
                    <span style={{ color: "#888" }}>—</span>
                  )}
                </td>
                <td>
                  <Badge outcome={row.outcome} />
                  {row.detail && (
                    <div
                      style={{
                        fontSize: "0.72rem",
                        color: "#888",
                        marginTop: 2,
                      }}
                    >
                      {row.detail}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error && (
        <p style={{ color: "#dc3545", fontSize: "0.875rem", margin: 0 }}>
          {error}
        </p>
      )}

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <SecondaryButton onClick={onBack} disabled={confirming}>
          ← Back
        </SecondaryButton>
        <PrimaryButton
          onClick={handleConfirm}
          disabled={confirming || createCount === 0}
        >
          {confirming
            ? "Importing…"
            : `Confirm & Import${createCount > 0 ? ` (${createCount})` : ""}`}
        </PrimaryButton>
      </div>
    </div>
  );
};

// ── Done step ─────────────────────────────────────────────────────────────────

const DoneStep = ({
  createdCount,
  onClose,
}: {
  createdCount: number;
  onClose: () => void;
}) => (
  <div className="text-center py-4">
    <h5 className="mb-3" style={{ color: "#495057" }}>
      Import Complete
    </h5>
    <p style={{ color: "#666" }}>
      {createdCount} Q&amp;A log{createdCount !== 1 ? "s" : ""} created.
    </p>
    <div className="mt-4">
      <SecondaryButton onClick={onClose}>Close</SecondaryButton>
    </div>
  </div>
);

// ── Dialog ────────────────────────────────────────────────────────────────────

interface Props {
  portfolioId: string;
  orgSlug: string;
  isOpen: boolean;
  onRequestClose: () => void;
}

export const QaLogImportDialog = ({
  portfolioId,
  orgSlug,
  isOpen,
  onRequestClose,
}: Props) => {
  const [step, setStep] = useState<Step>("UPLOAD");
  const [planRows, setPlanRows] = useState<PlanRow[]>([]);
  const [planFile, setPlanFile] = useState<File | null>(null);
  const [createdCount, setCreatedCount] = useState(0);

  const handleClose = () => {
    setStep("UPLOAD");
    setPlanRows([]);
    setPlanFile(null);
    onRequestClose();
  };

  return (
    <Dialog
      header="Import Q&A Logs"
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={{ content: { width: 720, maxWidth: "95vw" } }}
    >
      {step === "UPLOAD" && (
        <UploadStep
          portfolioId={portfolioId}
          orgSlug={orgSlug}
          onPlan={(rows, file) => {
            setPlanRows(rows);
            setPlanFile(file);
            setStep("REVIEW");
          }}
          onCancel={handleClose}
        />
      )}
      {step === "REVIEW" && planFile && (
        <ReviewStep
          portfolioId={portfolioId}
          rows={planRows}
          file={planFile}
          onConfirmed={(count) => {
            setCreatedCount(count);
            setStep("DONE");
          }}
          onBack={() => setStep("UPLOAD")}
        />
      )}
      {step === "DONE" && (
        <DoneStep createdCount={createdCount} onClose={handleClose} />
      )}
    </Dialog>
  );
};
